import { Boundaries } from "@planzone/core";
import { id, milestone, project, retrieveMilestoneError } from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { resetState } from "../../utils/resetState";
import { createStateReducer } from "../../utils/createStateReducer";
import { reduceList } from "../../common/list";
import { reduceProject } from "../../common/project";
import { reduceMilestone } from "../../common/milestone";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  milestoneId: Boundaries["retrieveMilestone"]["request"],
): State {
  const stateIndex = state.findIndex(({ id }) => id === milestoneId);

  return stateIndex !== -1
    ? [
        ...state.slice(0, stateIndex),
        {
          id: milestoneId,
          request: setRequestStateToPending(),
          milestone: { uuid: id, ...milestone, project },
          error: retrieveMilestoneError,
        },
        ...state.slice(stateIndex + 1),
      ]
    : [
        ...state,
        {
          id: milestoneId,
          request: setRequestStateToPending(),
          milestone: { uuid: id, ...milestone, project },
          error: retrieveMilestoneError,
        },
      ];
}

function handleSuccess(
  state: State,
  milestone: Boundaries["retrieveMilestone"]["success"],
): State {
  return state.map((item) =>
    item.id === milestone.uuid
      ? { ...item, milestone, request: setRequestStateToSuccess() }
      : item,
  );
}

function handleFailure(
  state: State,
  { milestoneId, error }: Boundaries["retrieveMilestone"]["failure"],
): State {
  return state.map((item) =>
    item.id === milestoneId
      ? { ...item, request: setRequestStateToFailure(), error }
      : item,
  );
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((item) => ({
    ...item,
    milestone: {
      ...item.milestone,
      ...reduceMilestone(item.milestone, event),
      list: reduceList(item.milestone.list, event),
      project: reduceProject(item.milestone.project, event),
    },
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveMilestoneRequested]: handleRequest,
      [events.retrieveMilestoneSucceeded]: handleSuccess,
      [events.retrieveMilestoneFailed]: handleFailure,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
